/**
 * Hero Slider Component
 * Industrial slider with autoplay and navigation controls.
 */
class HeroSlider {
    constructor(container) {
        this.container = container;
        this.slides = container.querySelectorAll('.hero-slider__slide');
        this.paginationDots = container.querySelectorAll('.hero-slider__pagination-dot');
        this.prevBtn = container.querySelector('.hero-slider__nav--prev');
        this.nextBtn = container.querySelector('.hero-slider__nav--next');

        this.currentSlide = 0;
        this.totalSlides = this.slides.length;
        this.autoPlayInterval = null;
        this.autoPlayDelay = 9000;
        this.isTransitioning = false;
        this.transitionTimer = null;

        this.init();
    }

    init() {
        if (this.totalSlides === 0) return;

        this.bindEvents();
        this.startAutoPlay();
        this.showSlide(0, { force: true });
        this.addAccessibilityAttributes();
        this.handleVisibilityChange();
    }

    bindEvents() {
        if (this.prevBtn) {
            this.prevBtn.addEventListener('click', () => this.prevSlide());
        }

        if (this.nextBtn) {
            this.nextBtn.addEventListener('click', () => this.nextSlide());
        }

        this.paginationDots.forEach((dot, index) => {
            dot.addEventListener('click', () => this.goToSlide(index));
        });

        this.container.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft') {
                this.prevSlide();
            } else if (e.key === 'ArrowRight') {
                this.nextSlide();
            } else if (e.key === 'Home') {
                this.goToSlide(0);
            } else if (e.key === 'End') {
                this.goToSlide(this.totalSlides - 1);
            }
        });

        this.addTouchSupport();

        this.container.addEventListener('mouseenter', () => this.pauseAutoPlay());
        this.container.addEventListener('mouseleave', () => this.startAutoPlay());

        this.container.addEventListener('focusin', () => this.pauseAutoPlay());
        this.container.addEventListener('focusout', () => this.startAutoPlay());
    }

    showSlide(index, options = {}) {
        const { force = false } = options;
        if (this.isTransitioning || (!force && index === this.currentSlide)) return;

        this.isTransitioning = true;
        this.container.classList.add('is-transitioning');
        if (this.transitionTimer) {
            clearTimeout(this.transitionTimer);
        }

        const currentSlideElement = this.slides[this.currentSlide];
        currentSlideElement.classList.remove('active');

        const newSlideElement = this.slides[index];
        newSlideElement.classList.add('active');

        this.updatePagination(index);
        this.currentSlide = index;

        this.transitionTimer = setTimeout(() => {
            this.isTransitioning = false;
            this.container.classList.remove('is-transitioning');
        }, 1200);
    }

    nextSlide() {
        const nextIndex = (this.currentSlide + 1) % this.totalSlides;
        this.showSlide(nextIndex);
        this.resetAutoPlay();
    }

    prevSlide() {
        const prevIndex = (this.currentSlide - 1 + this.totalSlides) % this.totalSlides;
        this.showSlide(prevIndex);
        this.resetAutoPlay();
    }

    goToSlide(index) {
        if (index >= 0 && index < this.totalSlides) {
            this.showSlide(index);
            this.resetAutoPlay();
        }
    }

    updatePagination(activeIndex) {
        this.paginationDots.forEach((dot, index) => {
            if (index === activeIndex) {
                dot.classList.add('active');
                dot.setAttribute('aria-current', 'true');
            } else {
                dot.classList.remove('active');
                dot.setAttribute('aria-current', 'false');
            }
        });
    }

    startAutoPlay() {
        if (this.autoPlayInterval) return;

        this.autoPlayInterval = setInterval(() => {
            this.nextSlide();
        }, this.autoPlayDelay);
    }

    pauseAutoPlay() {
        if (this.autoPlayInterval) {
            clearInterval(this.autoPlayInterval);
            this.autoPlayInterval = null;
        }
    }

    resetAutoPlay() {
        this.pauseAutoPlay();
        this.startAutoPlay();
    }

    addTouchSupport() {
        let startX = 0;
        let endX = 0;
        let isDragging = false;

        const handleStart = (e) => {
            startX = e.type === 'mousedown' ? e.clientX : e.touches[0].clientX;
            isDragging = true;
            this.pauseAutoPlay();
        };

        const handleMove = (e) => {
            if (!isDragging) return;
            endX = e.type === 'mousemove' ? e.clientX : e.touches[0].clientX;
        };

        const handleEnd = () => {
            if (!isDragging) return;

            const diff = startX - endX;
            const threshold = 50;

            if (Math.abs(diff) > threshold) {
                if (diff > 0) {
                    this.nextSlide();
                } else {
                    this.prevSlide();
                }
            }

            isDragging = false;
            this.startAutoPlay();
        };

        this.container.addEventListener('mousedown', handleStart);
        this.container.addEventListener('mousemove', handleMove);
        this.container.addEventListener('mouseup', handleEnd);
        this.container.addEventListener('mouseleave', handleEnd);

        this.container.addEventListener('touchstart', handleStart, { passive: true });
        this.container.addEventListener('touchmove', handleMove, { passive: true });
        this.container.addEventListener('touchend', handleEnd, { passive: true });
    }

    addAccessibilityAttributes() {
        this.container.setAttribute('role', 'region');
        this.container.setAttribute('aria-roledescription', 'carousel');
        this.container.setAttribute('aria-label', 'Промышленные лазерные решения');

        this.slides.forEach((slide, index) => {
            slide.setAttribute('role', 'group');
            slide.setAttribute('aria-roledescription', 'slide');
            slide.setAttribute('aria-label', `Слайд ${index + 1} из ${this.totalSlides}`);
        });

        if (this.prevBtn) {
            this.prevBtn.setAttribute('aria-label', 'Предыдущий слайд');
            this.prevBtn.setAttribute('aria-controls', 'hero-slider');
        }

        if (this.nextBtn) {
            this.nextBtn.setAttribute('aria-label', 'Следующий слайд');
            this.nextBtn.setAttribute('aria-controls', 'hero-slider');
        }

        this.paginationDots.forEach((dot, index) => {
            dot.setAttribute('role', 'button');
            dot.setAttribute('aria-label', `Перейти к слайду ${index + 1}`);
            dot.setAttribute('aria-controls', 'hero-slider');
            dot.setAttribute('tabindex', '0');
        });
    }

    handleVisibilityChange() {
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                this.pauseAutoPlay();
            } else {
                this.startAutoPlay();
            }
        });
    }

    pause() {
        this.pauseAutoPlay();
    }

    play() {
        this.startAutoPlay();
    }

    goTo(index) {
        this.goToSlide(index);
    }

    getCurrentSlide() {
        return this.currentSlide;
    }

    getTotalSlides() {
        return this.totalSlides;
    }

    destroy() {
        this.pauseAutoPlay();

        if (this.prevBtn) {
            this.prevBtn.removeEventListener('click', this.prevSlide);
        }

        if (this.nextBtn) {
            this.nextBtn.removeEventListener('click', this.nextSlide);
        }

        this.paginationDots.forEach((dot, index) => {
            dot.removeEventListener('click', () => this.goToSlide(index));
        });

        this.container.removeEventListener('keydown', this.handleKeyDown);
        this.container.removeEventListener('mouseenter', this.pauseAutoPlay);
        this.container.removeEventListener('mouseleave', this.startAutoPlay);

        this.container.removeAttribute('role');
        this.container.removeAttribute('aria-roledescription');
        this.container.removeAttribute('aria-label');
    }
}

document.addEventListener('DOMContentLoaded', () => {
    const heroSlider = document.querySelector('.hero-slider');
    if (heroSlider) {
        window.heroSlider = new HeroSlider(heroSlider);

        if (window.XTronWebApp) {
            window.XTronWebApp.heroSlider = window.heroSlider;
        }
    }
});

if (typeof module !== 'undefined' && module.exports) {
    module.exports = HeroSlider;
}
